import * as CryptoJS from "crypto-js";
import { CHUNK_SIZE } from "./types";
/**
 * Generate SHA256 hash for a File or ArrayBuffer
 */
export async function generateSHA256(data) {
    let buffer;
    if (data instanceof File) {
        buffer = await data.arrayBuffer();
    }
    else {
        buffer = data;
    }
    const uint8Array = new Uint8Array(buffer);
    const wordArray = CryptoJS.lib.WordArray.create(uint8Array);
    return CryptoJS.SHA256(wordArray).toString();
}
/**
 * Split file into chunks
 */
export async function* chunkFile(file, chunkSize = CHUNK_SIZE) {
    let offset = 0;
    let partNumber = 1;
    while (offset < file.size) {
        const size = Math.min(chunkSize, file.size - offset);
        const chunk = file.slice(offset, offset + size);
        yield {
            chunk,
            partNumber,
            offset,
            size,
        };
        offset += size;
        partNumber++;
    }
}
/**
 * Calculate total number of chunks for a file
 */
export function calculateTotalParts(fileSize, chunkSize = CHUNK_SIZE) {
    return Math.ceil(fileSize / chunkSize);
}
/**
 * Generate random link ID
 */
export function generateLinkId(filename) {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    const cleanFilename = filename
        .replace(/[^a-zA-Z0-9-_.]/g, "-")
        .substring(0, 20);
    return `${cleanFilename}-${timestamp}-${random}`;
}
/**
 * Generate password hash
 */
export function hashPassword(password) {
    return CryptoJS.SHA256(password).toString();
}
export function validateFile(file) {
    // Maximum file size: 2GB
    const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;
    if (file.size === 0) {
        return { valid: false, error: "File is empty" };
    }
    if (file.size > MAX_FILE_SIZE) {
        return { valid: false, error: "File too large (max 2GB)" };
    }
    // Check for potentially dangerous file extensions
    const dangerousExtensions = [".exe", ".bat", ".cmd", ".scr", ".pif", ".com"];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (dangerousExtensions.includes(fileExtension)) {
        return { valid: false, error: "File type not allowed" };
    }
    return { valid: true };
}
/**
 * Format file size for display
 */
export function formatFileSize(bytes) {
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    if (bytes === 0)
        return "0 B";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = bytes / Math.pow(1024, i);
    return `${size.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}
/**
 * Sleep utility for delays/backoff
 */
export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * Retry with exponential backoff
 */
export async function retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        }
        catch (error) {
            lastError = error;
            if (attempt === maxRetries) {
                throw lastError;
            }
            // Handle Discord rate limiting
            if (error instanceof Response && error.status === 429) {
                const response = error;
                const retryAfter = response.headers.get("Retry-After");
                const delay = retryAfter
                    ? parseInt(retryAfter) * 1000
                    : baseDelay * Math.pow(2, attempt);
                await sleep(delay);
            }
            else {
                await sleep(baseDelay * Math.pow(2, attempt));
            }
        }
    }
    throw lastError;
}
